import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface NavigationContextType {
  currentView: string;
  setCurrentView: (view: string) => void;
  selectedCourse: { id: string; name: string } | null;
  setSelectedCourse: (course: { id: string; name: string } | null) => void;
  navigateTo: (view: string) => void;
  goBack: () => void;
  resetNavigation: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

interface NavigationProviderProps {
  children: ReactNode;
  userId?: string | null;
}

export function NavigationProvider({ children, userId }: NavigationProviderProps) {
  const [currentView, setCurrentView] = useState<string>('dashboard');
  const [selectedCourse, setSelectedCourse] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    setCurrentView('dashboard');
    setSelectedCourse(null);
  }, [userId]);

  const navigateTo = (view: string) => {
    setSelectedCourse(null);
    setCurrentView(view);
  };

  const goBack = () => {
    if (selectedCourse) {
      setSelectedCourse(null);
    } else {
      setCurrentView('dashboard');
    }
  };

  const resetNavigation = () => {
    setCurrentView('dashboard');
    setSelectedCourse(null);
  };

  return (
    <NavigationContext.Provider
      value={{
        currentView,
        setCurrentView,
        selectedCourse,
        setSelectedCourse,
        navigateTo,
        goBack,
        resetNavigation,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}
